"use client"
import { useEffect } from "react";
import { createUserByGoogle, extractGoogleUserSessionFromResponse } from "../services/create-user-by-google";
import {
    hasStoredAuthToken,
    isGoogleAutoSignInSuppressed,
    persistAuthenticatedUserSession,
} from "../services/auth-storage";

function logGooglePromptMoment(notification) {
    if (!notification) {
        return;
    }

    let status = "displayed";
    let reason = "";

    if (typeof notification.isNotDisplayed === "function" && notification.isNotDisplayed()) {
        status = "not_displayed";
        reason = typeof notification.getNotDisplayedReason === "function" ? notification.getNotDisplayedReason() : "";
    } else if (typeof notification.isSkippedMoment === "function" && notification.isSkippedMoment()) {
        status = "skipped";
        reason = typeof notification.getSkippedReason === "function" ? notification.getSkippedReason() : "";
    } else if (typeof notification.isDismissedMoment === "function" && notification.isDismissedMoment()) {
        status = "dismissed";
        reason = typeof notification.getDismissedReason === "function" ? notification.getDismissedReason() : "";
    }

    if (status === "displayed") {
        return;
    }

    if (process.env.NODE_ENV !== "production") {
        console.info("[GoogleSignIn] One Tap prompt ended", { status, reason });
    }
}

function ensureGsiClientLoaded(cb) {
    const initGoogle = () => {
        if (!window.google?.accounts?.id) return;
        cb();
    };
    const existingScript = document.getElementById("google-gsi-script");
    if (!existingScript) {
        const script = document.createElement("script");
        script.id = "google-gsi-script";
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = initGoogle;
        document.head.appendChild(script);
    } else {
        initGoogle();
    }
}

async function defaultCallback(response) {
    if (!response?.credential || isGoogleAutoSignInSuppressed()) {
        return;
    }

    if (typeof createUserByGoogle !== "function") {
        return;
    }

    const userResponse = await createUserByGoogle({ accessToken: response.credential });
    const { accessToken = "", refreshToken = "", email = "" } = extractGoogleUserSessionFromResponse(userResponse);

    if (!accessToken) {
        return;
    }

    persistAuthenticatedUserSession({ accessToken, refreshToken, email });
}

export function GoogleSignInButton({ clientId, onSuccess = undefined, id = "google-btn", width = 250, size = "large", theme = "outline" }) {
    useEffect(() => {
        if (!clientId) return;

        ensureGsiClientLoaded(() => {
            if (!window.google?.accounts?.id) return;
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: onSuccess || defaultCallback,
            });
            const btnContainer = document.getElementById(id);
            if (btnContainer) {
                window.google.accounts.id.renderButton(btnContainer, {
                    theme,
                    size,
                    width,
                });
            }
        });
    }, [clientId, onSuccess, id, width, size, theme]);

    if (!clientId) return null;

    return (
        <div className="flex flex-col items-center space-y-3">
            <div id={id}></div>
        </div>
    );
}

export function AutoGoogleSignIn({ clientId, onSuccess = undefined }) {
    useEffect(() => {
        if (!clientId) return;
        if (hasStoredAuthToken()) return;
        if (isGoogleAutoSignInSuppressed()) return;

        ensureGsiClientLoaded(() => {
            if (!window.google?.accounts?.id) return;
            if (hasStoredAuthToken()) return;
            if (isGoogleAutoSignInSuppressed()) return;
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: onSuccess || defaultCallback,
            });
            window.google.accounts.id.prompt(logGooglePromptMoment);
        });
    }, [clientId, onSuccess]);

    return null;
}

// Backwards-compatible default export: button-rendering component
export default GoogleSignInButton;
